import { create } from 'zustand';
import { api, auth, uploadAudioFile } from '../lib/api';
import type {
  CaptureRow,
  CaptureSession,
  RowEditPatch,
  SessionSummary,
  SettingsSnapshot,
  TranscriptEvent,
  TranscriptionError,
  TranscriptionMode,
  TranscriptionProviderId,
  TranscriptionStatus,
  TranscriptionUtterance,
} from '@shared/types.js';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'user';
  mustChangePassword: boolean;
}

export type Toast = { id: number; kind: 'info' | 'success' | 'warn' | 'error'; text: string };

interface AppState {
  // auth
  authUser: AuthUser | null;
  authChecked: boolean;
  // catalog
  sessions: SessionSummary[];
  settings: SettingsSnapshot | null;
  // current session
  current: CaptureSession | null;
  rows: CaptureRow[];
  events: TranscriptEvent[];
  // ui
  view: 'capture' | 'review' | 'settings' | 'guide' | 'admin';
  showVoided: boolean;
  filter: 'all' | 'checked' | 'amended' | 'warning' | 'void';
  toasts: Toast[];

  // Transcription
  transcriptionStatus: TranscriptionStatus;
  transcriptionMode: TranscriptionMode;
  lastUtterance: TranscriptionUtterance | null;
  lastTranscriptionError: TranscriptionError | null;
  /** Registered by MicrophoneControl so other surfaces (e.g. Space in an
   * empty transcript box) can toggle dictation. */
  requestMicToggle: (() => void) | null;
  /** True while the Settings page holds edits that have not been saved.
   * Set by the Settings component; read by setView to warn on exit. */
  settingsDirty: boolean;

  // Auth actions
  bootstrapAuth(): Promise<void>;
  setAuthUser(u: AuthUser | null): void;
  signOut(): Promise<void>;

  refreshSessions(): Promise<void>;
  loadSettings(): Promise<void>;
  saveSettings(s: SettingsSnapshot): Promise<void>;
  createSession(name: string): Promise<void>;
  openSession(id: string): Promise<void>;
  renameSession(id: string, name: string): Promise<void>;
  deleteSession(id: string): Promise<void>;
  deleteAllSessions(): Promise<number>;
  setSessionContext(field: 'date' | 'feeEarner', value: string | null): Promise<void>;
  ingestLine(text: string, resolvesEventId?: string): Promise<void>;
  ingestBlock(text: string): Promise<void>;
  editRow(rowId: string, patch: RowEditPatch): Promise<void>;
  deleteRow(rowId: string): Promise<void>;
  addRow(draft: {
    description: string;
    units: number;
    date: string | null;
    feeEarner: string | null;
    section: string | null;
    reference: string | null;
  }): Promise<void>;
  reorderRows(orderedIds: string[]): Promise<void>;
  moveRow(rowId: string, direction: 'up' | 'down'): Promise<void>;
  setView(view: AppState['view']): void;
  setShowVoided(v: boolean): void;
  setFilter(f: AppState['filter']): void;
  toast(kind: Toast['kind'], text: string): void;
  dismissToast(id: number): void;

  // Transcription
  initTranscription(): () => void;
  startLiveTranscription(provider: TranscriptionProviderId, mode: TranscriptionMode): Promise<boolean>;
  stopLiveTranscription(): Promise<void>;
  setTranscriptionMode(mode: TranscriptionMode): void;
  setLastUtterance(u: TranscriptionUtterance | null): void;
  transcribeAudioFile(provider: TranscriptionProviderId, file: File): Promise<void>;
}

let toastId = 0;

export const useStore = create<AppState>((set, get) => ({
  authUser: null,
  authChecked: false,
  sessions: [],
  settings: null,
  current: null,
  rows: [],
  events: [],
  view: 'capture',
  showVoided: false,
  filter: 'all',
  toasts: [],

  transcriptionStatus: 'disconnected',
  transcriptionMode: 'push-to-talk',
  lastUtterance: null,
  lastTranscriptionError: null,
  requestMicToggle: null,
  settingsDirty: false,

  async bootstrapAuth() {
    try {
      const res = await auth.me();
      set({ authUser: res?.user ?? null, authChecked: true });
    } catch {
      set({ authUser: null, authChecked: true });
    }
  },
  setAuthUser(u) {
    set({ authUser: u });
  },
  async signOut() {
    try { await auth.logout(); } catch { /* ignore */ }
    set({
      authUser: null,
      sessions: [],
      current: null,
      rows: [],
      events: [],
      settings: null,
      view: 'capture',
    });
  },

  async refreshSessions() {
    const sessions = await api.listSessions();
    set({ sessions });
  },

  async loadSettings() {
    const settings = await api.getSettings();
    set({ settings });
  },

  async saveSettings(s) {
    const settings = await api.saveSettings(s);
    set({ settings });
    get().toast('success', 'Settings saved');
  },

  async createSession(name) {
    const session = await api.createSession(name);
    await get().refreshSessions();
    await get().openSession(session.id);
  },

  async openSession(id) {
    const { session, rows, events } = await api.openSession(id);
    set({ current: session, rows, events, view: 'capture' });
  },

  async renameSession(id, name) {
    const trimmed = name.trim();
    if (!trimmed) return;
    await api.renameSession(id, trimmed);
    await get().refreshSessions();
    const cur = get().current;
    if (cur && cur.id === id) {
      set({ current: { ...cur, name: trimmed } });
    }
  },

  async deleteSession(id) {
    await api.deleteSession(id);
    const cur = get().current;
    if (cur && cur.id === id) {
      set({ current: null, rows: [], events: [] });
    }
    await get().refreshSessions();
  },

  async deleteAllSessions() {
    const { deleted } = await api.deleteAllSessions();
    set({ current: null, rows: [], events: [] });
    await get().refreshSessions();
    return deleted;
  },

  async setSessionContext(field, value) {
    const cur = get().current;
    if (!cur) return;
    const session = await api.setContext(cur.id, field, value);
    set({ current: session });
  },

  async ingestLine(text, resolvesEventId) {
    const cur = get().current;
    if (!cur) return;
    const { event, rows, session } = await api.ingestLine(cur.id, text, resolvesEventId);
    set({ rows, current: session, events: [...get().events, event] });
    get().toast(...describeEvent(event));
    // A queue fix marks the ORIGINAL event resolved server-side; refresh
    // so it leaves the review queue immediately.
    if (resolvesEventId && event.eventType === 'row-added') {
      await get().openSession(cur.id);
    }
  },

  async ingestBlock(text) {
    const cur = get().current;
    if (!cur) return;
    const { events, rows, session } = await api.ingestBlock(cur.id, text);
    set({ rows, current: session, events: [...get().events, ...events] });
    const added = events.filter((e) => e.eventType === 'row-added').length;
    const skipped = events.filter((e) => e.eventType === 'unparsed').length;
    get().toast('info', `Pasted ${events.length} lines: +${added} rows, ${skipped} unparsed`);
  },

  async editRow(rowId, patch) {
    const cur = get().current;
    if (!cur) return;
    const { event, rows } = await api.editRow(cur.id, rowId, patch);
    set({ rows, events: [...get().events, event] });
    get().toast('info', 'Row edited');
  },

  async deleteRow(rowId) {
    const cur = get().current;
    if (!cur) return;
    const { rows } = await api.deleteRow(cur.id, rowId);
    set({ rows });
  },

  async addRow(draft) {
    const cur = get().current;
    if (!cur) return;
    const { row, event } = await api.addRow(cur.id, draft);
    set({ rows: [...get().rows, row], events: [...get().events, event] });
    get().toast('success', 'Row added');
  },

  async reorderRows(orderedIds) {
    const cur = get().current;
    if (!cur) return;
    // Optimistic local reorder for instant UI; server is source of truth
    // and we replace rows from its response when it returns.
    const byId = new Map(get().rows.map((r) => [r.id, r]));
    const optimistic = orderedIds
      .map((id, i) => {
        const r = byId.get(id);
        return r ? { ...r, rowNumber: i + 1 } : null;
      })
      .filter((r): r is NonNullable<typeof r> => r !== null);
    set({ rows: optimistic });
    const { rows } = await api.reorderRows(cur.id, orderedIds);
    set({ rows });
  },

  async moveRow(rowId, direction) {
    const rows = get().rows;
    const idx = rows.findIndex((r) => r.id === rowId);
    if (idx === -1) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= rows.length) return;
    const orderedIds = rows.map((r) => r.id);
    const [moved] = orderedIds.splice(idx, 1);
    if (!moved) return;
    orderedIds.splice(target, 0, moved);
    await get().reorderRows(orderedIds);
  },

  setView(view) {
    // Leaving Settings with unsaved edits used to discard them silently
    // — the draft resets on the next mount. Confirm first.
    const s = get();
    if (s.view === 'settings' && view !== 'settings' && s.settingsDirty) {
      const ok = window.confirm(
        'You have unsaved settings changes.\n\n' +
          'Leaving this page will discard them. Press Cancel, then "Save settings", to keep them.',
      );
      if (!ok) return;
      set({ settingsDirty: false });
    }
    set({ view });
  },
  setShowVoided(v) { set({ showVoided: v }); },
  setFilter(f) { set({ filter: f }); },

  toast(kind, text) {
    const id = ++toastId;
    set({ toasts: [...get().toasts, { id, kind, text }] });
    setTimeout(() => get().dismissToast(id), 4000);
  },
  dismissToast(id) {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },

  // ----- Transcription -----

  initTranscription() {
    // The audio upload's HTTP response now carries the utterance + outcome
    // directly (see MicrophoneControl.recordOnce), so we no longer rely on
    // SSE to deliver utterance/ingest events to the page. SSE remains
    // wired up for status pulses + error events which are best-effort.
    void api.transcriptionStatus().then((s) => set({ transcriptionStatus: s }));
    const offStatus = api.onTranscriptionStatus((s) => {
      set({ transcriptionStatus: s });
    });
    const offError = api.onTranscriptionError((err) => {
      set({ lastTranscriptionError: err });
      get().toast('error', `Transcription: ${err.code} — ${err.message}`);
    });
    return () => {
      offStatus();
      offError();
    };
  },

  async startLiveTranscription(provider, mode) {
    const cur = get().current;
    if (!cur) {
      get().toast('warn', 'Open a session first');
      return false;
    }
    set({ transcriptionMode: mode });
    try {
      await api.transcriptionStartLive({ sessionId: cur.id, provider, mode });
      return true;
    } catch (err) {
      get().toast('error', `Could not start: ${(err as Error).message}`);
      return false;
    }
  },

  async stopLiveTranscription() {
    try {
      await api.transcriptionStopLive();
    } catch (err) {
      get().toast('error', `Could not stop: ${(err as Error).message}`);
    }
  },

  setTranscriptionMode(mode) { set({ transcriptionMode: mode }); },
  setLastUtterance(u) { set({ lastUtterance: u }); },

  async transcribeAudioFile(provider, file) {
    const cur = get().current;
    if (!cur) {
      get().toast('warn', 'Open a session first');
      return;
    }
    try {
      const { rowsAdded, events } = await uploadAudioFile({
        sessionId: cur.id,
        provider,
        file,
      });
      get().toast('success', `Transcribed: +${rowsAdded} rows, ${events} lines`);
      await get().openSession(cur.id);
    } catch (err) {
      get().toast('error', `Transcription failed: ${(err as Error).message}`);
    }
  },
}));

function describeEvent(ev: TranscriptEvent): [Toast['kind'], string] {
  switch (ev.eventType) {
    case 'row-added':
      return ['success', `Added: ${shortRow(ev.parserResult)}`];
    case 'row-voided':
      return ['warn', `Voided last row`];
    case 'row-restored':
      return ['success', `Restored last voided row`];
    case 'row-checked':
      return ['info', `Checked last row`];
    case 'row-unchecked':
      return ['info', `Unchecked last row`];
    case 'row-amended':
      return ['info', `Amended: ${ev.previousValue ?? ''} → ${ev.newValue ?? ''}`];
    case 'context-change':
      return ['info', `Context: ${ev.newValue}`];
    case 'unparsed':
      return ['warn', `Unparsed — ${ev.newValue ?? 'see review queue'}`];
    case 'ignored':
      return ['info', 'Ignored'];
    case 'row-edited':
      return ['info', 'Row manually edited'];
  }
}

function shortRow(parserResult: TranscriptEvent['parserResult']): string {
  if (parserResult.kind !== 'row') return '';
  const r = parserResult.row;
  return `${r.description} — ${r.units} unit${r.units === 1 ? '' : 's'} (${r.decimalTime})`;
}
